import { SeriesOptionsType } from "highcharts";
import { ChartAxisProps, ChartDataSeries, ChartType } from "../../../components/Chart";
import { ReportingPeriods } from "../../../types";
import { getMonthNameAbbreviated } from "../../../utils/monthNames";
import ApiClient from "../../api";

export type CovenantsNetTotalLeverageChartData = {
  series: Array<ChartDataSeries>;
  xAxis: ChartAxisProps;
  yAxis: ChartAxisProps;
};

export type CovenantsNetTotalLeverageDetail = {
  financialYear: number;
  quarter: number;
  month: number;
  year: number;
  value: number;
};

export type CovenantsNetTotalLeverageDBData = {
  proForma: {
    dataSeries: Array<CovenantsNetTotalLeverageDetail>;
  };
  complianceCertificate: {
    dataSeries: Array<CovenantsNetTotalLeverageDetail>;
  };
  covenantTest: {
    dataSeries: Array<CovenantsNetTotalLeverageDetail>;
  };
};

export const getCovenantsNetTotalLeverageChartData = async (
  companyId: number,
  reportingCycle: ReportingPeriods,
  financialYear: number,
  enableConversions: boolean = false,
  unit: string = ""
) => {
  const queryString = `?type=${
    ChartType.COVENANTS_NET_TOTAL_LEVERAGE
  }&companyId=${companyId}&reportingCycle=${reportingCycle}&financialYear=${financialYear}&enableConversions=${
    enableConversions ? "true" : "false"
  }&unit=${unit ? unit : ""}`;

  const seriesFromDB = await ApiClient.get(`/charts${queryString}`);
  if (!seriesFromDB.data) return null;

  let xAxis: ChartAxisProps | null = null;
  const xAxisTitles = await getXAxisTitles(seriesFromDB.data, reportingCycle);

  if (xAxisTitles) {
    xAxis = {
      titles: xAxisTitles,
      colors: [],
    };
  }

  const yAxis: ChartAxisProps = {
    titles: [],
    colors: [],
  };

  const series = await getSeries(seriesFromDB.data, reportingCycle);

  return {
    series,
    xAxis,
    yAxis,
  } as CovenantsNetTotalLeverageChartData;
};

const getXAxisTitles = async (data: CovenantsNetTotalLeverageDBData, reportingCycle: ReportingPeriods) => {
  let detailToUse: CovenantsNetTotalLeverageDetail[];

  // using the first arrays with data
  if (data.complianceCertificate.dataSeries?.length > 0) {
    detailToUse = data.complianceCertificate.dataSeries;
  } else if (data.covenantTest.dataSeries?.length > 0) {
    detailToUse = data.covenantTest.dataSeries;
  } else if (data.proForma.dataSeries?.length > 0) {
    detailToUse = data.proForma.dataSeries;
  } else {
    return [];
  }

  detailToUse = detailToUse.sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));

  if (reportingCycle === ReportingPeriods.Quarterly) {
    let dataToReturn = [];
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)}`);
    }
    return dataToReturn;
  } else if (reportingCycle === ReportingPeriods.Monthly) {
    let dataToReturn = [];
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)}`);
    }
    return dataToReturn;
  } else {
    return [];
  }
};

// Use getSeries to decorate the series with specific elements to the chart.
const getSeries = async (data: CovenantsNetTotalLeverageDBData, reportingCycle: ReportingPeriods) => {
  const series: SeriesOptionsType[] = [];
  series.push({
    animation: false,
    name: "Pro Forma",
    data: await getRawDataFromSeries(data.proForma.dataSeries, reportingCycle),
    color: "#8DA93D",
    type: "column",
    borderRadius: 0,
  });
  series.push({
    animation: false,
    name: "As Per Compliance Certificate",
    data: await getRawDataFromSeries(data.complianceCertificate.dataSeries, reportingCycle),
    color: "#5a6b2d",
    type: "column",
    borderRadius: 0,
  });
  series.push({
    animation: false,
    name: "Covenant Test",
    data: await getRawDataFromSeries(data.covenantTest.dataSeries, reportingCycle),
    color: "#4FBCA8",
    type: "column",
    borderRadius: 0,
  });
  return series;
};

const getRawDataFromSeries = async (data: CovenantsNetTotalLeverageDetail[], reportingCycle: ReportingPeriods) => {
  if (!data || data.length === 0) return [];

  let rawDataToReturn = [];

  switch (reportingCycle) {
    case ReportingPeriods.Quarterly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    case ReportingPeriods.Monthly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    default:
      return [];
  }
};
